import React, {
  ComponentType,
  useLayoutEffect,
  useRef,
  type FC,
  type PropsWithChildren,
} from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { all, AllSystemProps } from '@moonpig/launchpad-system'
import { Link as LinkComponent } from '@moonpig/launchpad-components'
import { COLOR_LINK_HOVER, COLOR_DEFAULT_TEXT } from '../constants'

const LinkWithFocusRef = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof LinkComponent> & {
    focusRef?: React.Ref<HTMLAnchorElement>
  }
>(({ focusRef, ...rest }, _) => <LinkComponent {...rest} ref={focusRef} />)

const withFocus = <P,>(
  WrappedComponent: ComponentType<P & { focusRef?: React.Ref<HTMLElement> }>,
): FC<PropsWithChildren<P & { isFocused?: boolean }>> => {
  return ({ isFocused, ...rest }) => {
    const focusRef = useRef<HTMLElement>(null)

    useLayoutEffect(() => {
      if (isFocused) {
        focusRef.current?.focus()
      }
    }, [isFocused])

    return <WrappedComponent {...(rest as P)} focusRef={focusRef} />
  }
}

export const Link = styled(withFocus(LinkWithFocusRef))<AllSystemProps>`
  ${all}
  color: ${COLOR_DEFAULT_TEXT};
  display: block;

  &:hover,
  &:focus {
    color: ${COLOR_LINK_HOVER};
  }
`
